// Embedded-apps runtime — open-apps store (VW-MIG-S1.D3 / CFR-VW-145).
//
// ARCHITECTURE DECISION (operator, 2026-07-14): the redesign's keep-alive (`display:none` multi-mount)
// is NOT built. VW already solved "don't lose the user's place" with AppSession last-screen restore
// (`models/app_session.py`, cat 48.16 — DB-persisted per (actor,app,scope)), and keep-alive was
// explicitly REJECTED on record there (`app_session.py:15`). So the strip runs on the RESTORE path:
// this store tracks the open SET + active app for the strip UI; activating a chip navigates to the
// app's mount route, which remounts and restores its last screen from AppSession. Cleaner + reliable:
// reuses shipped, DB-persisted, policy-clean infra (survives refresh AND device switch), avoids the
// memory/timer cost of many hidden-live mounts. The only thing restore doesn't preserve — transient
// unsaved in-screen inputs — is intentionally not persisted (spec §3); keep-alive would only be
// revisited if that becomes a hard requirement.
//
// Pure client-state: open / activate / close / reorder, active id. No client-storage persistence
// (VW bans localStorage/sessionStorage); the open-set is session-scoped in memory, and each app's
// position is already DB-persisted by AppSession — so nothing durable is dropped.
import { create } from "zustand";
import { ZARVIS_APP_ID } from "@perlab/registry";
import { cancelZarvisBackgroundTurnsForWorkspace } from "../inspector/zarvisBackgroundTurns";

// Ordinary app instances are mounted at a concrete scope node. Zarvis is the shell-owned exception:
// one tray tab belongs to the workspace, while `scopeNodeId` records its last selected client or
// engagement so activating the tab can restore that routed scope.
export interface OpenApp {
  appId: string;
  workspaceId: string;
  scopeNodeId: string;
  name: string;
  icon: string;
  /** DtV's current session destination, retained per open scope tab in memory. */
  sessionRoute?: { session?: string; surface?: string };
}

export type OpenAppIdentity = Pick<OpenApp, "appId" | "workspaceId" | "scopeNodeId">;

/** Canonical tray identity: Zarvis is grouped per workspace; every other app remains per scope. */
export const openAppKey = (app: OpenAppIdentity): string => app.appId === ZARVIS_APP_ID
  ? `${app.appId}@workspace:${app.workspaceId}`
  : `${app.appId}@${app.scopeNodeId}`;
const keyOf = (app: OpenApp): string => openAppKey(app);

function withPinnedSurfaceFirst(open: OpenApp[]): OpenApp[] {
  return [
    ...open.filter((app) => app.appId === ZARVIS_APP_ID),
    ...open.filter((app) => app.appId !== ZARVIS_APP_ID),
  ];
}

export interface OpenAppsState {
  /** Open apps in strip order (oldest-first). Inactive apps are unmounted; re-activating remounts
   *  and restores the app's last screen via AppSession (restore path, not keep-alive). */
  open: OpenApp[];
  /** The active (foreground) app's key, or null when the directory/none is shown. */
  activeKey: string | null;
  /** Open an app (or re-activate its canonical instance). Activates it. */
  openApp: (app: OpenApp) => void;
  /** Activate an already-open app by key (no-op if not open). */
  activateApp: (key: string) => void;
  /** Close one app; if it was active, activate the previous remaining app (or null). */
  closeApp: (key: string) => void;
  /** Close all (e.g. "Apps home"/reset). */
  closeAll: () => void;
  /** Is this canonical app instance currently open? */
  isOpen: (key: string) => boolean;
}

export const useOpenApps = create<OpenAppsState>((set, get) => ({
  open: [],
  activeKey: null,
  openApp: (app) =>
    set((s) => {
      const key = keyOf(app);
      // Refresh the route/display snapshot when an instance already exists. For workspace-grouped
      // Zarvis, this keeps `scopeNodeId` aligned with the latest in-workspace selection.
      const existingIndex = s.open.findIndex((current) => keyOf(current) === key);
      if (existingIndex !== -1) {
        const updated = s.open.map((current, index) => index === existingIndex ? app : current);
        return { open: withPinnedSurfaceFirst(updated), activeKey: key };
      }
      return {
        open: withPinnedSurfaceFirst([...s.open, app]),
        activeKey: key,
      };
    }),
  activateApp: (key) =>
    set((s) => (s.open.some((a) => keyOf(a) === key) ? { activeKey: key } : {})),
  closeApp: (key) => {
    const target = get().open.find((app) => keyOf(app) === key);
    if (target?.appId === ZARVIS_APP_ID) {
      cancelZarvisBackgroundTurnsForWorkspace(target.workspaceId);
    }
    set((s) => {
      const idx = s.open.findIndex((a) => keyOf(a) === key);
      if (idx === -1) return {};
      const open = s.open.filter((_, i) => i !== idx);
      let activeKey = s.activeKey;
      if (s.activeKey === key) {
        // Fall back to the previous app in strip order (or the new first, or none).
        const fallback = open[idx - 1] ?? open[idx] ?? open[open.length - 1];
        activeKey = fallback ? keyOf(fallback) : null;
      }
      return { open, activeKey };
    });
  },
  closeAll: () => {
    for (const app of get().open) {
      if (app.appId === ZARVIS_APP_ID) {
        cancelZarvisBackgroundTurnsForWorkspace(app.workspaceId);
      }
    }
    set({ open: [], activeKey: null });
  },
  isOpen: (key) => get().open.some((a) => keyOf(a) === key),
}));
